import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import { withSize } from 'react-sizeme';
import Lightbox from 'react-image-lightbox';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import LazyLoad from 'components/LazyLoad/LazyLoad';
import { isEqual, omit } from 'lodash';
import 'react-image-lightbox/style.css';

class Thumbnail extends Component {
  static propTypes = {
    aspectRatio: PropTypes.string,
    circle: PropTypes.bool,
    className: PropTypes.string,
    color: PropTypes.string,
    id: PropTypes.string,
    size: PropTypes.object,
    square: PropTypes.bool,
    original: PropTypes.bool,
    style: PropTypes.object,
    thumbnails: PropTypes.object.isRequired,
    temp: PropTypes.string,
    type: PropTypes.string,
    zoom: PropTypes.bool,
    onClick: PropTypes.func,
    onZoom: PropTypes.func,
    onLoad: PropTypes.func,
    boxShadow: PropTypes.string,
    lazyLoad: PropTypes.bool,
  };

  static defaultProps = {
    aspectRatio: null,
    circle: undefined, // should be false, temp hack to suppress console warnings
    className: '',
    square: undefined, // should be false, temp hack to suppress console warnings
    style: {},
    zoom: undefined, // should be false, temp hack to suppress console warnings
    temp: '',
    boxShadow: '',
    lazyLoad: true,
  };

  state = {
    zoomed: false,
  };

  imageLayerRef = createRef();
  revealedSrc = null;

  shouldComponentUpdate(nextProps, nextState) {
    const nextWidth = nextProps.size && nextProps.size.width;
    const prevWidth = this.props.size && this.props.size.width;

    return (
      Math.floor(nextWidth || 0) !== Math.floor(prevWidth || 0) ||
      !isEqual(this.state, nextState) ||
      !isEqual(nextProps.thumbnails, this.props.thumbnails)
    );
  }

  getClosestImage(size) {
    const images = this.props.thumbnails.images;
    const keys = Object.keys(images);
    const width = size || 0;

    const filteredKeys = keys.filter((key) => images[key].width > width);

    let max = 0;
    if (filteredKeys.length <= 0) {
      max = keys.slice(-1).pop();
    } else {
      max = filteredKeys.shift();
    }

    return images[max];
  }

  // Fade via DOM (no setState) so image loads do not re-render the tree /
  // thrash react-sizeme, which can jump the page scroll position.
  revealImage = (loadedSrc) => {
    if (!loadedSrc || this.revealedSrc === loadedSrc) {
      return;
    }

    this.revealedSrc = loadedSrc;
    if (this.imageLayerRef.current) {
      this.imageLayerRef.current.style.opacity = '1';
    }
    if (this.props.onLoad) {
      this.props.onLoad();
    }
  };

  handleImageRef = (img) => {
    // Cached images may already be complete when the node mounts.
    if (img && img.complete && img.naturalWidth > 0) {
      this.revealImage(img.getAttribute('src') || img.src);
    }
  };

  render() {
    if (!this.props.thumbnails || !this.props.thumbnails.images) {
      return <noscript />;
    }

    const color = this.props.color || this.props.thumbnails.color;
    const measuredWidth = this.props.size && this.props.size.width;
    // Wait for react-sizeme so we pick the right src (and avoid a 0-width
    // fetch) before mounting image content. Colour layer still paints.
    const hasMeasuredWidth = typeof measuredWidth === 'number';
    let image = this.getClosestImage(measuredWidth);

    if (typeof image === 'undefined') {
      return <noscript />;
    }

    const useSquare =
      this.props.square ||
      this.props.aspectRatio === '1:1' ||
      typeof image.wide === 'undefined';

    if (useSquare && image.square) {
      image = image.square;
    } else if (image.wide) {
      image = image.wide;
    } else {
      image = image.square;
    }

    if (!image) {
      return <noscript />;
    }

    if (this.props.type) {
      image = image[this.props.type];
    } else {
      image = image.main;
    }

    const ratios = {
      '16:9': 56.25,
      '1:1': 100,
      '4:3': 75,
      '3:2': 66.66,
      '8:5': 62.5,
    };
    let ratioSize = ratios[this.props.aspectRatio] || null;

    const original =
      this.props.thumbnails.original_optimised !== ''
        ? this.props.thumbnails.original_optimised
        : this.props.thumbnails.original;

    if (this.props.original && original) {
      image = original;
      ratioSize = 100;
    }

    const styles = {
      borderRadius: this.props.circle ? '50%' : '0',
      overflow: 'hidden',
      position: 'relative',
      ...this.props.style,
      ...(ratioSize ? { width: '100%', paddingBottom: `${ratioSize}%` } : {}),
      ...(this.props.zoom && original ? { cursor: 'zoom-in' } : {}),
    };

    const displayUrl = this.props.thumbnails.temp
      ? this.props.thumbnails.temp
      : image;

    // Fade in once the image has loaded. Disable LazyLoad's mount-time
    // fade animation so it does not fight this load-based transition.
    // Opacity is also set imperatively in revealImage; keep this in sync so
    // later React re-renders do not stomp a revealed image back to 0.
    const imageLayerStyle = {
      backgroundImage: 'url(' + displayUrl + ')',
      backgroundSize: this.props.original ? 'contain' : 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      borderRadius: this.props.circle ? '50%' : '0',
      bottom: 0,
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      opacity: this.revealedSrc === image ? 1 : 0,
      transition: 'opacity 250ms ease',
      animation: 'none',
      ...(this.props.boxShadow ? { boxShadow: this.props.boxShadow } : {}),
    };

    // Fill the thumbnail so IntersectionObserver has a real box (not 0x0).
    const lazyFillStyle = {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      animation: 'none',
    };

    const imageContent = (
      <div style={lazyFillStyle}>
        <div
          ref={this.imageLayerRef}
          data-testid="thumbnail-image"
          style={imageLayerStyle}
        />
        <img
          alt="Twine"
          src={image}
          style={{ display: 'none' }}
          ref={this.handleImageRef}
          onLoad={() => this.revealImage(image)}
        />
      </div>
    );

    // Strip component-only props so they are not forwarded to the root DOM node.
    const rest = omit(this.props, [
      'aspectRatio',
      'circle',
      'boxShadow',
      'square',
      'thumbnails',
      'size',
      'type',
      'zoom',
      'onClick',
      'onZoom',
      'onLoad',
      'temp',
      'lazyLoad',
      'color',
      'original',
      'className',
      'style',
      'id',
    ]);

    return (
      <div
        {...rest}
        className={this.props.className ? this.props.className : ''}
        style={styles}
        id={this.props.id}
      >
        <div
          onClick={() => {
            if (this.props.onClick) {
              return this.props.onClick();
            }

            this.setState({
              zoomed:
                this.props.zoom &&
                original &&
                (this.props.onZoom ? this.props.onZoom() : true)
                  ? true
                  : false,
            });
          }}
        >
          <div
            style={{
              backgroundColor: color,
              borderRadius: this.props.circle ? '50%' : '0',
              bottom: 0,
              left: 0,
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          />
          {hasMeasuredWidth &&
            (this.props.lazyLoad ? (
              <LazyLoad style={lazyFillStyle}>{imageContent}</LazyLoad>
            ) : (
              imageContent
            ))}
          {this.props.zoom && original ? (
            <SvgIcon
              icon="zoom"
              color="white"
              size="20"
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                marginRight: '10px',
                marginBottom: '10px',
                transition: 'opacity ease 0.5s',
              }}
            />
          ) : null}
        </div>
        {this.state.zoomed ? (
          <Lightbox
            mainSrc={original}
            discourageDownloads
            onCloseRequest={() => this.setState({ zoomed: false })}
          />
        ) : null}
      </div>
    );
  }
}

// 'object' strategy avoids element-resize-detector's scroll listeners, which
// can jump window scroll when many thumbnails mount/resize near page bottom.
export default withSize({
  resizeDetectorStrategy: 'object',
  noPlaceholder: true,
})(Thumbnail);
